/**
 * Vocabulário do produto, compartilhado entre API e telas.
 * Os valores (chaves) batem com os enums do banco em packages/db/src/schema.ts.
 */
export { FAIXAS, FAIXA_LABEL, type Faixa } from '../triagem/qualificacao';
export { IMOVEIS, IMOVEL_LABEL, OBJETIVO_LABEL, PRAZO_LABEL, PAGAMENTO_LABEL, PROPRIEDADE_LABEL, LOCAL_LABEL } from '../triagem/opcoes';
export { REGIOES, REGIAO_LABEL, formatCep } from '../triagem/regiao';

/** Etapas do funil, na ordem. As consultas do admin comparam etapas com >= e <. */
export const FUNNEL_STAGES = [
  'iniciou',
  'objetivo_informado',
  'perfil_informado',
  'localizacao_informada',
  'fora_do_perfil',
  'qualificado',
  'consentiu_contato',
  'enviado_whatsapp',
] as const;
export type FunnelStageKey = (typeof FUNNEL_STAGES)[number];

export function stageAtLeast(stage: FunnelStageKey, min: FunnelStageKey): boolean {
  return FUNNEL_STAGES.indexOf(stage) >= FUNNEL_STAGES.indexOf(min);
}

export const FUNNEL_STAGE_LABEL: Record<FunnelStageKey, string> = {
  iniciou: 'Iniciou',
  objetivo_informado: 'Disse o que procura',
  perfil_informado: 'Informou imóvel e conta',
  localizacao_informada: 'Informou a localização',
  fora_do_perfil: 'Fora do perfil',
  qualificado: 'Qualificado',
  consentiu_contato: 'Consentiu o contato',
  enviado_whatsapp: 'Enviado ao WhatsApp',
};

/** Por que a conversa terminou sem WhatsApp. Guardado no lead e mostrado no admin. */
export const MOTIVOS = ['conta_baixa', 'apartamento_sem_area', 'fora_da_area'] as const;
export type MotivoKey = (typeof MOTIVOS)[number];
export const MOTIVO_LABEL: Record<MotivoKey, string> = {
  conta_baixa: 'Conta de luz abaixo do mínimo',
  apartamento_sem_area: 'Apartamento sem área própria',
  fora_da_area: 'Fora da área atendida',
};

/** Tipo de atendimento que o lead pediu. */
export const OBJETIVOS = ['instalar', 'manutencao'] as const;
export type ObjetivoKey = (typeof OBJETIVOS)[number];

/** Status comercial, marcado pela equipe da Sun Coast no painel. */
export const COMMERCIAL_STATUSES = ['novo', 'em_contato', 'visita_agendada', 'proposta_enviada', 'fechado', 'nao_fechou'] as const;
export type CommercialStatusKey = (typeof COMMERCIAL_STATUSES)[number];
export const STATUS_LABEL: Record<CommercialStatusKey, string> = {
  novo: 'Novo',
  em_contato: 'Em contato',
  visita_agendada: 'Visita agendada',
  proposta_enviada: 'Proposta enviada',
  fechado: 'Fechado',
  nao_fechou: 'Não fechou',
};

/**
 * Etapa exibida no admin (coluna "Etapa"). Junta o funil com o status comercial
 * e marca como "Abandonou" quem parou antes do fim da triagem e sumiu.
 */
export const DISPLAY_STAGES = [
  'iniciou',
  'respondendo',
  'fora_do_perfil',
  'qualificado',
  'enviado_whatsapp',
  'em_atendimento',
  'convertido',
  'nao_fechou',
  'abandonou',
] as const;
export type DisplayStageKey = (typeof DISPLAY_STAGES)[number];
export const DISPLAY_STAGE_LABEL: Record<DisplayStageKey, string> = {
  iniciou: 'Iniciou o chat',
  respondendo: 'Respondendo',
  fora_do_perfil: 'Fora do perfil',
  qualificado: 'Qualificado, sem contato',
  enviado_whatsapp: 'Enviado ao WhatsApp',
  em_atendimento: 'Em atendimento',
  convertido: 'Convertido',
  nao_fechou: 'Não fechou',
  abandonou: 'Abandonou',
};

/** Horas sem atividade antes do fim da triagem para considerar abandono. */
export const ABANDON_AFTER_HOURS = 24;

export function displayStage(
  lead: { stage: FunnelStageKey; commercialStatus: CommercialStatusKey | null; lastActivityAt: Date | string },
  now: Date = new Date(),
): DisplayStageKey {
  if (lead.commercialStatus === 'fechado') return 'convertido';
  if (lead.commercialStatus === 'nao_fechou') return 'nao_fechou';
  if (lead.commercialStatus && lead.commercialStatus !== 'novo') return 'em_atendimento';
  if (lead.stage === 'enviado_whatsapp' || lead.stage === 'consentiu_contato') return 'enviado_whatsapp';
  if (lead.stage === 'fora_do_perfil') return 'fora_do_perfil';
  const idleMs = now.getTime() - new Date(lead.lastActivityAt).getTime();
  if (idleMs > ABANDON_AFTER_HOURS * 3600_000) return 'abandonou';
  if (lead.stage === 'qualificado') return 'qualificado';
  return lead.stage === 'iniciou' ? 'iniciou' : 'respondendo';
}

// ---------------------------------------------------------------------------
// Dinheiro e telefone
// ---------------------------------------------------------------------------

/** "12.345,67" ou "12345.67" ou "12345" -> centavos. Retorna null se inválido. */
export function parseBRLToCents(input: string): number | null {
  const s = input.replace(/\s|R\$/g, '');
  if (!s) return null;
  let normalized: string;
  if (s.includes(',')) normalized = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) normalized = s.replace(/\./g, '');
  else normalized = s;
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const cents = Math.round(Number(normalized) * 100);
  return Number.isSafeInteger(cents) ? cents : null;
}

export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Mantém só dígitos. Aceita com ou sem 55. Retorna formato 55DDNNNNNNNNN ou null. */
export function normalizeWhatsapp(input: string): string | null {
  let d = input.replace(/\D/g, '');
  if (d.length === 10 || d.length === 11) d = `55${d}`;
  return /^55\d{10,11}$/.test(d) ? d : null;
}

/**
 * Celular brasileiro com DDD: 11 dígitos, DDD de 11 a 99 e nono dígito 9. Aceita +55.
 * Devolve 55DDNNNNNNNNN ou null. É a regra do formulário do chat (cliente e servidor).
 */
export function normalizeCelular(input: string): string | null {
  let d = input.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) d = d.slice(2);
  return /^[1-9][1-9]9\d{8}$/.test(d) ? `55${d}` : null;
}

/** Máscara de digitação: (11) 91234-5678. Não valida; só formata até 11 dígitos. */
export function maskCelularInput(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** (11) 9••••-4127 */
export function maskPhone(whatsapp: string | null): string {
  if (!whatsapp) return '—';
  const d = whatsapp.replace(/^55/, '');
  const ddd = d.slice(0, 2);
  const last = d.slice(-4);
  return `(${ddd}) ${d.length === 11 ? '9' : ''}••••-${last}`;
}

/** (11) 98765-4127 */
export function formatPhone(whatsapp: string | null): string {
  if (!whatsapp) return '—';
  const d = whatsapp.replace(/^55/, '');
  const ddd = d.slice(0, 2);
  const rest = d.slice(2);
  return rest.length === 9
    ? `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`
    : `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
}

// ---------------------------------------------------------------------------
// Regras de status comercial
// ---------------------------------------------------------------------------

export interface StatusChangeInput {
  status?: CommercialStatusKey;
  closedValueCents?: number | null;
}

/**
 * Valida a mudança e devolve o estado final.
 * - "Fechado" exige o valor do projeto, maior que zero.
 * - Fora de "Fechado", o valor é apagado.
 */
export function applyStatusChange(
  current: { status: CommercialStatusKey | null; closedValueCents: number | null },
  change: StatusChangeInput,
): { ok: true; status: CommercialStatusKey | null; closedValueCents: number | null } | { ok: false; error: string } {
  const status = change.status ?? current.status;
  if (!status) return { ok: false, error: 'Lead ainda não enviou o contato.' };
  if (status !== 'fechado') return { ok: true, status, closedValueCents: null };
  const value = change.closedValueCents !== undefined ? change.closedValueCents : current.closedValueCents;
  if (value == null || value <= 0) return { ok: false, error: 'Informe o valor do projeto fechado.' };
  if (value > 100_000_000_00) return { ok: false, error: 'Valor do projeto fora do limite.' };
  return { ok: true, status, closedValueCents: value };
}

// ---------------------------------------------------------------------------
// Constantes de filtros e senha (sem Zod, para não pesar nas telas)
// ---------------------------------------------------------------------------

export const PASSWORD_MIN = 12;

export const PERIODS = ['7d', '30d', '90d', 'tudo'] as const;
export type PeriodKey = (typeof PERIODS)[number];
export const PERIOD_LABEL: Record<PeriodKey, string> = {
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  '90d': 'Últimos 90 dias',
  tudo: 'Todo o período',
};

export const LEAD_FILTERS = ['todos', 'whatsapp', 'quentes', 'senior', 'manutencao', 'convertido', 'abandonou'] as const;
export type LeadFilterKey = (typeof LEAD_FILTERS)[number];
export const LEAD_FILTER_LABEL: Record<LeadFilterKey, string> = {
  todos: 'Todos',
  whatsapp: 'Enviados ao WhatsApp',
  quentes: 'Quentes',
  senior: 'Consultor sênior',
  manutencao: 'Manutenção',
  convertido: 'Convertidos',
  abandonou: 'Abandonaram',
};

/** Origem sem UTM. */
export const DIRECT_SOURCE = '(direto)';

export const USER_ROLES = ['admin', 'comercial'] as const;
export type UserRoleKey = (typeof USER_ROLES)[number];
export const ROLE_LABEL: Record<UserRoleKey, string> = { admin: 'Administrador', comercial: 'Comercial' };
